import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { NavigationService, ProfileService } from '@outlaw/core';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogWrapperComponent } from '../dialog-wrapper/dialog-wrapper.component';
import { LazyLoadListComponent } from '../../lazy-load-list/lazy-load-list.component';
import { LikedUser, PostUser, RecommendedByDialogData, ShortFollowedUserResponse } from '../../../interfaces';
import { FollowerService } from '../../../services/api/followers.service';
import { PostService } from '../../../services/api/post.service';
import { DialogRef } from '@ngneat/dialog';
import { DialogManagerService } from '../../../services/dialog/dialog-manager.service';
import { FollowMemberComponent } from '../../follow-member/follow-member.component';
import { EXTERNAL_PROFILE_SERVICE } from '../../../providers/external-profile.provider';
import { FeatureFlagService, CommunityMobileEventsService } from '../../../services';
import { ExternalPostNavigationService } from '../../../services/navigation/external-post-navigation.service';

const MIN_MEMBERS_SCROLL_ITEMS = 5;

@Component({
  selector: 'app-recommended-by-dialog',
  templateUrl: './recommended-by-dialog.component.html',
  styleUrls: ['./recommended-by-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DialogWrapperComponent, LazyLoadListComponent, FollowMemberComponent],
})
export class RecommendedByDialogComponent {
  #dialogRef = inject<DialogRef<RecommendedByDialogData>>(DialogRef);
  #profileService = inject(ProfileService);
  #postService = inject(PostService);
  #followersService = inject(FollowerService);
  #dialogManagerService = inject(DialogManagerService);
  #navigationService = inject(NavigationService);
  #destroyRef = inject(DestroyRef);
  #cdr = inject(ChangeDetectorRef);
  #externalProfileService = inject(EXTERNAL_PROFILE_SERVICE, {
    optional: true,
  });
  #mobileEventsService = inject(CommunityMobileEventsService);
  #externalPostNavService = inject(ExternalPostNavigationService);

  openProfileInNewWindow: boolean = inject(FeatureFlagService).isShowProfileLinksInRecommendedByDialog();

  isLoading = false;
  members: LikedUser[] = [];

  isMajiApp: boolean;
  meId: string;
  postUUID: string;
  postOwner: PostUser | null;

  get dialogData(): RecommendedByDialogData {
    return this.#dialogRef.data;
  }

  get hasMembers(): boolean {
    return this.members.length > 0;
  }

  get hasMembersScroll(): boolean {
    return this.members.length >= MIN_MEMBERS_SCROLL_ITEMS;
  }

  constructor() {
    const { isMajiApp, postUUID, postOwner } = this.dialogData;
    this.isMajiApp = isMajiApp;
    this.postUUID = postUUID;
    this.postOwner = postOwner;
    this.meId = this.#profileService.getUser()?.passport_uuid ?? '';
  }

  fetchItems(): void {
    if (this.isLoading || this.members.length) {
      return;
    }

    this.isLoading = true;
    this.#postService
      .getUsersLikedPosts(this.postUUID)
      .pipe(
        switchMap(members => this.processMembersData(members)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: members => (this.members = members),
      });
  }

  openUserProfile(member: LikedUser): void {
    if (this.#externalProfileService) {
      this.openExternalProfile(member);
    } else {
      this.openInternalProfile(member);
    }
  }

  private processMembersData(members: LikedUser[]): Observable<LikedUser[]> {
    if (!this.postOwner) {
      if (!members.length) {
        return of([]);
      }
      const userUUIDs = members.map(item => item.passport_uuid);
      return this.getFollowersIntersections(userUUIDs, members);
    }

    if (!this.meId) {
      return of(members);
    }

    const isOwnerInList = members.some(m => m.passport_uuid === this.postOwner.user_uuid);

    if (isOwnerInList) {
      const userUUIDs = members.map(item => item.passport_uuid);
      return this.getFollowersIntersections(userUUIDs, members);
    }

    return this.#followersService.isFollowing(this.postOwner.user_uuid).pipe(
      catchError(() => of({ is_following: false })),
      switchMap(followStatus => {
        const ownerAsLikedUser: LikedUser = {
          passport_uuid: this.postOwner.user_uuid,
          username: this.postOwner.username,
          avatar_url: this.#externalPostNavService.getUserLogoUrl(this.postOwner.user_logo) || this.postOwner.avatar_url,
          types: this.postOwner.types,
          is_following: followStatus.is_following,
          followed_by: [],
        };

        const allMembers = [ownerAsLikedUser, ...members];
        const userUUIDs = allMembers.map(item => item.passport_uuid);
        return this.getFollowersIntersections(userUUIDs, allMembers);
      })
    );
  }

  private openExternalProfile(member: LikedUser): void {
    this.#externalProfileService!.getProfilePageUrl(member.passport_uuid)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(url => {
        if (url) {
          this.#navigationService.openOnExternalPage(url);
        } else {
          this.openInternalProfile(member);
        }
      });
  }

  private openInternalProfile(member: LikedUser): void {
    if (this.#mobileEventsService.isWebView) {
      return;
    }

    const dialogResult = this.#dialogManagerService.openUserProfile(
      member.passport_uuid,
      member.username,
      this.openProfileInNewWindow
    );

    if (dialogResult) {
      dialogResult.afterClosed$.pipe(take(1), takeUntilDestroyed(this.#destroyRef)).subscribe(result => {
        if (result) {
          this.updateUserFollowingStatus(member.passport_uuid);
        }
      });
    }
  }

  private getFollowersIntersections(userUUIDs: string[], members: LikedUser[]): Observable<LikedUser[]> {
    return this.#followersService.listIntersectionsBulk(userUUIDs).pipe(
      catchError(() => of({})),
      map(follows =>
        members.map(item => ({
          ...item,
          followed_by: (follows as ShortFollowedUserResponse)[item.passport_uuid] || [],
        }))
      )
    );
  }

  private updateUserFollowingStatus(userUUID: string): void {
    const memberIndex = this.members.findIndex(member => member.passport_uuid === userUUID);

    if (memberIndex === -1) {
      return;
    }

    this.members = this.members.map((member, index) =>
      index === memberIndex ? { ...member, is_following: !member.is_following } : member
    );

    this.#cdr.markForCheck();
  }
}
